
import { useState, useEffect } from 'react';
import { 
  Activity, Calendar, Brain, Heart, 
  MessageSquare, Bell, User, Clock,
  Video
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeatureIconProps {
  icon: React.ReactNode;
  active: boolean;
  index: number;
  totalIcons: number;
}

const FeatureIcon = ({ icon, active, index, totalIcons }: FeatureIconProps) => {
  // Calculate position on the circle
  const angle = (index / totalIcons) * 2 * Math.PI;
  const radius = 130; // Circle radius in pixels
  const x = Math.round(radius * Math.cos(angle));
  const y = Math.round(radius * Math.sin(angle));

  return (
    <div 
      className={cn(
        "absolute transform transition-all duration-500 ease-in-out",
        "flex items-center justify-center rounded-full",
        "bg-white shadow-lg p-3 z-10",
        active ? "scale-125 bg-blue-100" : "scale-100"
      )}
      style={{ 
        transform: `translate(${x}px, ${y}px) ${active ? 'scale(1.25)' : 'scale(1)'}`,
        transition: 'all 0.5s ease',
        left: '50%',
        top: '50%',
        marginLeft: '-20px',
        marginTop: '-20px'
      }}
    >
      <div className={cn(
        "text-primary",
        active ? "text-blue-600" : "text-gray-500"
      )}>
        {icon}
      </div>
    </div>
  );
};

const FeaturesCircle = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const features = [
    { name: "Suivi santé", icon: <Activity size={24} /> },
    { name: "Rendez-vous", icon: <Calendar size={24} /> },
    { name: "IA médicale", icon: <Brain size={24} /> },
    { name: "Suivi grossesse", icon: <Heart size={24} /> },
    { name: "Messagerie", icon: <MessageSquare size={24} /> },
    { name: "Notifications", icon: <Bell size={24} /> },
    { name: "Profil médical", icon: <User size={24} /> },
    { name: "Rappels", icon: <Clock size={24} /> },
    { name: "Téléconsultation", icon: <Video size={24} /> }
  ];
  
  useEffect(() => {
    // Rotate through icons every second
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % features.length);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [features.length]);
  
  return (
    <div className="relative w-full h-[400px] flex items-center justify-center">
      {/* Rotating circle animation */}
      <div className="absolute w-[300px] h-[300px] rounded-full border-4 border-dashed border-blue-200 animate-spin" style={{ animationDuration: '20s' }}></div>
      <div className="absolute w-[270px] h-[270px] rounded-full border-2 border-blue-100"></div>
      
      {/* Center pulse animation */}
      <div className="absolute w-20 h-20 bg-blue-500/10 rounded-full animate-pulse"></div>
      <div className="absolute w-14 h-14 bg-primary rounded-full flex items-center justify-center text-white z-20">
        <span className="font-bold">Medicace</span>
      </div>
      
      {/* Highlighted feature name */}
      <div className="absolute -bottom-10 text-center text-lg font-medium text-blue-600">
        {features[activeIndex].name}
      </div>
      
      {/* Feature icons */}
      <div className="absolute w-full h-full">
        {features.map((feature, index) => (
          <FeatureIcon 
            key={index}
            icon={feature.icon}
            active={index === activeIndex}
            index={index}
            totalIcons={features.length}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturesCircle;
